import {
  EquipmentType,
  GLCode,
  Mode,
  Money,
  UserRef,
  ZonedDateTimeRange,
} from '@rygen/rygen-ui/model';
import { Shipment as EsShipment } from '@rygen/tms-search-model/search/shipment';

import { ClientSummaryDto } from '../ClientSummaryDto';
import { ReferenceNumber } from '../ReferenceNumber';

export class ShipmentSearchData {
  id?: number;
  originLocationName?: string;
  originLocality?: string;
  originRegion?: string;
  originPostalCode?: string;
  originLocationGroupIds?: string[];
  destinationLocationName?: string;
  destinationLocality?: string;
  destinationRegion?: string;
  destinationPostalCode?: string;
  destinationLocationGroupIds?: string[];
  client?: ClientSummaryDto;
  direction?: string;
  billingTerms?: string;
  equipmentType?: EquipmentType;
  freightAllowanceEnabled: boolean = false;
  freightAllowance?: Money;
  stopCount?: number;
  status?: string;
  health?: string;
  financialStatus?: string;
  scheduledPickupDateRange?: ZonedDateTimeRange;
  requestedPickupDateRange?: ZonedDateTimeRange;
  pickupAppointmentRequired: boolean = false;
  scheduledDeliveryDateRange?: ZonedDateTimeRange;
  requestedDeliveryDateRange?: ZonedDateTimeRange;
  deliveryAppointmentRequired: boolean = false;
  carrierName?: string;
  scac?: string;
  costEstimate?: Money;
  service?: string;
  mode?: string;
  expectedPickupArrivalDate?: Date;
  actualPickupArrivalDate?: Date;
  actualPickupDepartureDate?: Date;
  expectedDeliveryArrivalDate?: Date;
  actualDeliveryArrivalDate?: Date;
  actualDeliveryDepartureDate?: Date;
  multistop?: boolean = false;
  referenceNumbers?: ReferenceNumber[];
  lineItemReferenceNumbers?: ReferenceNumber[];
  relatedReferenceNumbers?: ReferenceNumber[];
  relatedGlCodes?: GLCode[];
  tags?: string[];
  assignedUsers?: UserRef[];
  weight?: number;
  weightUom?: string;
  handlingUnitCount?: number;
  handlingUnitDescription?: string;
  productNumber?: string;
  productDescription?: string;
  hot: boolean = false;
  hazmat: boolean = false;
  payable: boolean = false;
  spotMarketRequestCount: number = 0;
  spotMarketAcceptedCount: number = 0;
  cancellationReason?: string;
  requestedMode?: Mode;
  source?: string;
  swc: boolean = false;
  customerRouted: boolean = false;
  totalEstimatedMileage?: number;
  quotedMileage?: number;

  constructor(opts?: Partial<ShipmentSearchData>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.originLocationName != null) {
      this.originLocationName = opts.originLocationName;
    }
    if (opts?.originLocality != null) {
      this.originLocality = opts.originLocality;
    }
    if (opts?.originRegion != null) {
      this.originRegion = opts.originRegion;
    }
    if (opts?.originPostalCode != null) {
      this.originPostalCode = opts.originPostalCode;
    }
    if (opts?.originLocationGroupIds != null) {
      this.originLocationGroupIds = [...opts.originLocationGroupIds];
    }
    if (opts?.destinationLocationName != null) {
      this.destinationLocationName = opts.destinationLocationName;
    }
    if (opts?.destinationLocality != null) {
      this.destinationLocality = opts.destinationLocality;
    }
    if (opts?.destinationRegion != null) {
      this.destinationRegion = opts.destinationRegion;
    }
    if (opts?.destinationPostalCode != null) {
      this.destinationPostalCode = opts.destinationPostalCode;
    }
    if (opts?.destinationLocationGroupIds != null) {
      this.destinationLocationGroupIds = [...opts.destinationLocationGroupIds];
    }
    if (opts?.client != null) {
      this.client = new ClientSummaryDto(opts.client);
    }
    if (opts?.direction != null) {
      this.direction = opts.direction;
    }
    if (opts?.billingTerms != null) {
      this.billingTerms = opts.billingTerms;
    }
    if (opts?.equipmentType != null) {
      this.equipmentType = new EquipmentType(opts.equipmentType);
    }
    if (opts?.stopCount != null) {
      this.stopCount = opts.stopCount;
    }
    if (opts?.status != null) {
      this.status = opts.status;
    }
    if (opts?.health != null) {
      this.health = opts.health;
    }
    if (opts?.freightAllowanceEnabled != null) {
      this.freightAllowanceEnabled = opts.freightAllowanceEnabled;
    }
    if (opts?.freightAllowance) {
      this.freightAllowance = new Money(opts.freightAllowance);
    }
    if (opts?.financialStatus != null) {
      this.financialStatus = opts.financialStatus;
    }
    if (opts?.scheduledPickupDateRange != null) {
      this.scheduledPickupDateRange = new ZonedDateTimeRange(
        opts.scheduledPickupDateRange,
      );
    }
    if (opts?.requestedPickupDateRange != null) {
      this.requestedPickupDateRange = new ZonedDateTimeRange(
        opts.requestedPickupDateRange,
      );
    }
    if (opts?.pickupAppointmentRequired != null) {
      this.pickupAppointmentRequired = opts.pickupAppointmentRequired;
    }
    if (opts?.scheduledDeliveryDateRange != null) {
      this.scheduledDeliveryDateRange = new ZonedDateTimeRange(
        opts.scheduledDeliveryDateRange,
      );
    }
    if (opts?.requestedDeliveryDateRange != null) {
      this.requestedDeliveryDateRange = new ZonedDateTimeRange(
        opts.requestedDeliveryDateRange,
      );
    }
    if (opts?.deliveryAppointmentRequired != null) {
      this.deliveryAppointmentRequired = opts.deliveryAppointmentRequired;
    }
    if (opts?.carrierName != null) {
      this.carrierName = opts.carrierName;
    }
    if (opts?.scac != null) {
      this.scac = opts.scac;
    }
    if (opts?.costEstimate != null) {
      this.costEstimate = new Money(opts.costEstimate);
    }
    if (opts?.service != null) {
      this.service = opts.service;
    }
    if (opts?.mode != null) {
      this.mode = opts.mode;
    }
    if (opts?.expectedPickupArrivalDate != null) {
      this.expectedPickupArrivalDate = opts.expectedPickupArrivalDate;
    }
    if (opts?.actualPickupArrivalDate != null) {
      this.actualPickupArrivalDate = opts.actualPickupArrivalDate;
    }
    if (opts?.expectedDeliveryArrivalDate != null) {
      this.expectedDeliveryArrivalDate = opts.expectedDeliveryArrivalDate;
    }
    if (opts?.actualDeliveryArrivalDate != null) {
      this.actualDeliveryArrivalDate = opts.actualDeliveryArrivalDate;
    }
    if (opts?.actualPickupDepartureDate != null) {
      this.actualPickupDepartureDate = opts.actualPickupDepartureDate;
    }
    if (opts?.actualDeliveryDepartureDate != null) {
      this.actualDeliveryDepartureDate = opts.actualDeliveryDepartureDate;
    }
    if (opts?.multistop != null) {
      this.multistop = opts.multistop;
    }
    if (opts?.referenceNumbers != null) {
      this.referenceNumbers = opts.referenceNumbers.map(
        (r) => new ReferenceNumber(r),
      );
    }
    if (opts?.lineItemReferenceNumbers != null) {
      this.lineItemReferenceNumbers = opts.lineItemReferenceNumbers.map(
        (r) => new ReferenceNumber(r),
      );
    }
    if (opts?.relatedReferenceNumbers != null) {
      this.relatedReferenceNumbers = opts.relatedReferenceNumbers.map(
        (r) => new ReferenceNumber(r),
      );
    }
    if (opts?.relatedGlCodes != null) {
      this.relatedGlCodes = opts.relatedGlCodes.map((g) => new GLCode(g));
    }
    if (opts?.tags != null) {
      this.tags = opts.tags;
    }
    if (opts?.assignedUsers != null) {
      this.assignedUsers = opts.assignedUsers.map((val) => new UserRef(val));
    }

    if (opts?.weight != null) {
      this.weight = opts.weight;
    }
    if (opts?.weightUom != null) {
      this.weightUom = opts.weightUom;
    }
    if (opts?.handlingUnitCount != null) {
      this.handlingUnitCount = opts.handlingUnitCount;
    }
    if (opts?.handlingUnitDescription != null) {
      this.handlingUnitDescription = opts.handlingUnitDescription;
    }
    if (opts?.productNumber != null) {
      this.productNumber = opts.productNumber;
    }
    if (opts?.productDescription != null) {
      this.productDescription = opts.productDescription;
    }
    if (opts?.hot != null) {
      this.hot = opts.hot;
    }
    if (opts?.hazmat != null) {
      this.hazmat = opts.hazmat;
    }
    if (opts?.payable != null) {
      this.payable = opts.payable;
    }
    if (opts?.spotMarketRequestCount) {
      this.spotMarketRequestCount = opts.spotMarketRequestCount;
    }
    if (opts?.spotMarketAcceptedCount) {
      this.spotMarketAcceptedCount = opts.spotMarketAcceptedCount;
    }
    if (opts?.cancellationReason != null) {
      this.cancellationReason = opts.cancellationReason;
    }
    if (opts?.requestedMode != null) {
      this.requestedMode = opts.requestedMode;
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.swc != null) {
      this.swc = opts.swc;
    }
    if (opts?.customerRouted != null) {
      this.customerRouted = opts.customerRouted;
    }
    if (opts?.totalEstimatedMileage != null) {
      this.totalEstimatedMileage = opts.totalEstimatedMileage;
    }
    if (opts?.quotedMileage != null) {
      this.quotedMileage = opts.quotedMileage;
    }
  }

  static isOldSearchModel(
    searchResult: ShipmentSearchData | EsShipment,
  ): searchResult is ShipmentSearchData {
    return 'scac' in searchResult;
  }

  static isOldSearchArray(
    searchResults: ShipmentSearchData[] | EsShipment[],
  ): searchResults is ShipmentSearchData[] {
    return ShipmentSearchData.isOldSearchModel(searchResults[0]);
  }

  get totalMileage(): number | undefined {
    return this.quotedMileage ?? this.totalEstimatedMileage;
  }

  get owner() {
    return this.client;
  }
}
